import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardShell from "@/components/DashboardShell";
import StudentsTab from "@/components/admin/StudentsTab";
import ComplaintsTab from "@/components/admin/ComplaintsTab";
import GalleryTab from "@/components/admin/GalleryTab";
import RequestsTab from "@/components/admin/RequestsTab";
import RoomsTab from "@/components/admin/RoomsTab";
import MetersTab from "@/components/admin/MetersTab";
import MenuTab from "@/components/admin/MenuTab";
import EnquiriesTab from "@/components/admin/EnquiriesTab";
import ReceiptsTab from "@/components/admin/ReceiptsTab";
import ReceiptTemplateTab from "@/components/admin/ReceiptTemplateTab";
import BulkRemindersTab from "@/components/admin/BulkRemindersTab";
import { api } from "@/lib/api";
import { Users, Wallet, AlertTriangle, DoorOpen, Bell, UserPlus } from "lucide-react";

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "requests", label: "Requests" },
  { key: "enquiries", label: "Enquiries" },
  { key: "students", label: "Students" },
  { key: "rooms", label: "Rooms" },
  { key: "meters", label: "Meters" },
  { key: "complaints", label: "Complaints" },
  { key: "reminders", label: "Reminders" },
  { key: "receipts", label: "Receipts" },
  { key: "template", label: "Receipt Template" },
  { key: "menu", label: "Menu" },
  { key: "gallery", label: "Gallery" },
];

function StatCard({ label, value, tone, icon: Icon, testId }) {
  const toneMap = {
    yellow: { bg: "#FFE600", fg: "#0A0A0A" },
    coral: { bg: "#FF4D4D", fg: "#FFFFFF" },
    cyan: { bg: "#00E5FF", fg: "#0A0A0A" },
    green: { bg: "#00CC66", fg: "#FFFFFF" },
    ink: { bg: "#0A0A0A", fg: "#FFE600" },
  };
  const t = toneMap[tone] || { bg: "#FFFFFF", fg: "#0A0A0A" };
  return (
    <div
      data-testid={testId}
      style={{
        backgroundColor: t.bg,
        color: t.fg,
        border: "2px solid #0A0A0A",
        borderRadius: "0.75rem",
        boxShadow: "4px 4px 0px 0px rgba(10,10,10,1)",
        padding: "1.5rem",
        transition: "transform 200ms, box-shadow 200ms",
      }}
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-[0.2em]">{label}</span>
        <Icon size={22} strokeWidth={2.5} />
      </div>
      <div className="font-heading font-black text-4xl tracking-tighter mt-3">{value}</div>
    </div>
  );
}

export default function AdminDashboard() {
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);

  const loadStats = () =>
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
  const loadPending = () =>
    api.get("/admin/pending-users").then((r) => setPendingCount((r.data || []).length)).catch(() => {});

  useEffect(() => {
    loadStats();
    loadPending();
  }, []);

  return (
    <DashboardShell title="Admin Dashboard" subtitle="Vasavi Boys Hostel" testId="admin-dashboard">
      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-8" data-testid="admin-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`admin-tab-${t.key}`}
            className={`relative px-5 py-2.5 font-bold text-sm border-2 border-[#0A0A0A] rounded-full transition-transform ${
              tab === t.key
                ? "bg-[#0A0A0A] text-[#FFE600] shadow-brutal-sm"
                : "bg-white hover:shadow-brutal-sm"
            }`}
          >
            {t.label}
            {t.key === "requests" && pendingCount > 0 && (
              <span className="ml-2 inline-flex items-center justify-center min-w-[1.4rem] h-5 px-1.5 border-2 border-[#0A0A0A] rounded-full bg-[#FF4D4D] text-white text-[10px] font-bold">
                {pendingCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="space-y-8" data-testid="overview-tab">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
            <StatCard
              label="Total Members"
              value={stats?.total_students ?? "-"}
              tone="yellow"
              icon={Users}
              testId="stat-total-students"
            />
            <StatCard
              label="Fees Collected"
              value={`₹${(stats?.total_fees_collected ?? 0).toLocaleString()}`}
              tone="green"
              icon={Wallet}
              testId="stat-fees-collected"
            />
            <StatCard
              label="Dues Pending"
              value={`₹${(stats?.total_dues ?? 0).toLocaleString()}`}
              tone="coral"
              icon={AlertTriangle}
              testId="stat-dues-pending"
            />
            <StatCard
              label="Occupied Rooms"
              value={stats?.occupied_flats ?? "-"}
              tone="cyan"
              icon={DoorOpen}
              testId="stat-occupied-rooms"
            />
            <StatCard
              label="Open Complaints"
              value={stats?.pending_complaints ?? "-"}
              tone="ink"
              icon={Bell}
              testId="stat-pending-complaints"
            />
          </div>
          <div className="brutal-card p-8 bg-white">
            <h3 className="font-heading font-black text-2xl">Quick actions</h3>
            <p className="mt-2 font-medium text-[#4A4A4A]">
              Head to the tabs above to manage students, respond to complaints, or update the public
              gallery.
            </p>
          </div>
        </div>
      )}

      {tab === "students" && <StudentsTab onChange={loadStats} />}
      {tab === "rooms" && <RoomsTab />}
      {tab === "meters" && <MetersTab />}
      {tab === "complaints" && <ComplaintsTab onChange={loadStats} />}
      {tab === "reminders" && <BulkRemindersTab />}
      {tab === "receipts" && <ReceiptsTab />}
      {tab === "template" && <ReceiptTemplateTab />}
      {tab === "menu" && <MenuTab />}
      {tab === "gallery" && <GalleryTab />}
      {tab === "requests" && <RequestsTab onChange={loadPending} />}
      {tab === "enquiries" && <EnquiriesTab />}
    </DashboardShell>
  );
}
